import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { MusicScheduleServices } from "../../../core/services/MusicScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import {
  MusicScheduleFields,
  MusicScheduleListParams,
  Permission,
} from "./MusicScheduleDataTypes";
import "./MusicSchedules.css";
import MusicScheduleFilters from "./MusicScheduleFilters";
import { getTodayShortDay, isEmptyVal } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import MusicScheduleSingleAction from "./MusicScheduleSingleAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  scheduleForTime,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import MusicScheduleInfoCell from "../../components/Elements/MusicScheduleInfoCell";
import MusicScheduleSongs from "./MusicScheduleSongs";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function MusicSchedules() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MusicScheduleFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [songSearchParams, setSongSearchParams] = useState<any>({
    smart_search: "",
    play_for_day: getTodayShortDay().toLowerCase(),
    time_slot: "",
  });
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedMusicSchedule, setSelectedMusicSchedule] = useState<any>({});

  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [tabView, setTabView] = useState(scheduleForTime.current.value);

  const initSearchFormData = {
    name: "",
    company: "",
    store_group: "",
    is_default: "",
    new_schedule_everyday: "",
    start_date: null,
    end_date: null,
    period_nature: "",
    song_count: "",
    song_search: "",
    schedule_date: "",
    store: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<MusicScheduleListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const [musicScheduleFetched, setMusicScheduleFetched] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  const [failedImages, setFailedImages] = useState<any>([]);
  const publishPermission = UtilityServices.hasPermission(
    "publish-music-schedule"
  );
  const updatePermission = UtilityServices.hasPermission(
    "update-music-schedule"
  );
  const deletePermission = UtilityServices.hasPermission(
    "delete-music-schedule"
  );
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
  };

  function getParamsFromTab(tabView: string) {
    let tParams: any = {};
    switch (tabView) {
      case scheduleForTime.current.value:
        tParams.period_nature = scheduleForTime.current.value;
        break;
      case scheduleForTime.upcoming.value:
        tParams.period_nature = scheduleForTime.upcoming.value;
        break;
      case scheduleForTime.past.value:
        tParams.period_nature = scheduleForTime.past.value;
        break;
      case scheduleForTime.all.value:
        break;
    }
    return tParams;
  }

  function selectMusicSchedule(
    resData: any,
    selectMusicScheduleId: number = 0
  ) {
    setSelectedMusicSchedule({});
    if (resData.length > 0) {
      if (selectMusicScheduleId == 0) {
        setSelectedMusicSchedule(resData[0]);
      } else {
        let musicScheduleToSelect = resData
          .filter((item) => item.id == selectMusicScheduleId)
          .shift();
        if (musicScheduleToSelect) {
          setSelectedMusicSchedule(musicScheduleToSelect);
        } else {
          setSelectedMusicSchedule(resData[0]);
        }
      }
    }
  }

  async function getMusicSchedules(
    params: MusicScheduleListParams,
    selectMusicScheduleId: number = 0
  ) {
    try {
      let compId: number = getCompanyId();
      if (!compId) {
        setData([]);
        setMusicScheduleFetched(false);
        return;
      }

      setLoading(true);
      setMusicScheduleFetched(false);

      params = {
        ...params,
        ...getParamsFromTab(tabView),
        ...{ company: compId },
      };
      const res = await MusicScheduleServices.getMusicSchedules(params);
      const resData = get("data.data", res);
      setData(resData);
      setTotal(get("data.total", res));
      selectMusicSchedule(resData, selectMusicScheduleId);
      setCheckedKeys([]);
      setLoading(false);
      setMusicScheduleFetched(true);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      setShowCreate(UtilityServices.hasPermission("create-music-schedule"));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteMusicSchedule(musicSchedule: any) {
    try {
      let res = await MusicScheduleServices.deleteMusicSchedule(
        musicSchedule?.id
      );
      setPage(1);
      getMusicSchedules(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function fetchMusicScheduleSongs(musicSchedule: any) {
    setSelectedMusicSchedule(musicSchedule);
  }

  async function publishMusicSchedule(musicSchedule: any) {
    try {
      let status =
        musicSchedule?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;
      let res = await MusicScheduleServices.publishMusicSchedule(
        musicSchedule?.id,
        {
          status,
        }
      );
      setPage(1);
      getMusicSchedules(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...data,
    };

    let params: MusicScheduleListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function processCreateSchedule() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    navigate(ContentRoutes.CREATE_MUSIC_SCHEDULE);
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onPageChange(page: number) {
    setPage(page);
    getMusicSchedules(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getMusicSchedules(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getMusicSchedules(getSearchParams(formData));
  }

  function submitSearchAfterSongChange(
    prevMusicScheduleId: any,
    sngSearchParams?: any
  ) {
    setPage(page);
    getMusicSchedules(getSearchParams(formData), prevMusicScheduleId);
    sngSearchParams && setSongSearchParams(sngSearchParams);
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getMusicSchedules(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterMusicSchedules = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getMusicSchedules(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getMusicSchedules(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getMusicSchedules(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const [showColumns, setShowColumns] = useState([
    { fieldName: t("IMAGE") },
    { fieldName: t("NAME") },
  ]);

  useEffect(() => {
    getMusicSchedules(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
    switch (tabView) {
      case scheduleForTime.current.value:
        setShowColumns([
          { fieldName: t("NAME") },
          { fieldName: t("STORE_GROUP") },
        ]);
        break;
      case scheduleForTime.upcoming.value:
        setShowColumns([
          { fieldName: t("NAME") },
          { fieldName: t("STORE_GROUP") },
        ]);
        break;
      case scheduleForTime.past.value:
        setShowColumns([
          { fieldName: t("NAME") },
          { fieldName: t("STORE_GROUP") },
        ]);
        break;
      case scheduleForTime.all.value:
        setShowColumns([
          { fieldName: t("NAME") },
          { fieldName: t("STORE_GROUP") },
        ]);
        break;
      default:
        break;
    }
  }, [tabView, company]);

  return (
    <div className="music-schedules customers">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("MUSIC_SCHEDULES")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateSchedule}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="music-schedules-content">
          <MusicScheduleFilters
            tabViewSelected={tabView}
            onSubmit={submitSearch}
            onClear={clearSearch}
          />
          <div className="music-schedules-split-layout">
            <div
              className={
                total > 0
                  ? "music-schedule-list-panel usr-tbl-border"
                  : "music-schedule-list-panel no-record-table"
              }
            >
              <div className="row music-schedule-list-search-row">
                <div className="col-md-12">
                  <InputGroup className="music-schedule-list-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("MUSIC_SCHEDULE").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterMusicSchedules(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterMusicSchedules(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div className="music-schedule-tabs">
                <button
                  style={{
                    opacity:
                      tabView === scheduleForTime.current.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.current.value)}
                >
                  {scheduleForTime.current.label}
                </button>
                <button
                  style={{
                    opacity:
                      tabView === scheduleForTime.upcoming.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.upcoming.value)}
                >
                  {scheduleForTime.upcoming.label}
                </button>
                <button
                  style={{
                    opacity: tabView === scheduleForTime.past.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.past.value)}
                >
                  {scheduleForTime.past.label}
                </button>
                <button
                  style={{
                    opacity: tabView === scheduleForTime.all.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.all.value)}
                >
                  {scheduleForTime.all.label}
                </button>
              </div>

              <div className="music-schedule-table">
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onRowClick={fetchMusicScheduleSongs}
                  rowClassName={(rowData: any) =>
                    rowData?.id === selectedMusicSchedule?.id
                      ? "music-schedule-row-selected"
                      : ""
                  }
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  rowHeight={80}
                  hover={true}
                  renderEmpty={() => (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      {getCompanyId() !== null ? (
                        <>
                          <h4>
                            {t("NO_ENTITY_FOUND", {
                              entity: t("MUSIC_SCHEDULES"),
                            })}
                          </h4>
                          <p>{t("ADJUST_FILTER")}</p>
                        </>
                      ) : (
                        <h4>
                          {t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                        </h4>
                      )}
                    </div>
                  )}
                >
                  {showColumns?.length &&
                    showColumns.map((field: any, index: number) => {
                      return (
                        <>
                          {field.fieldName === t("NAME") ? (
                            <Column
                              width={220}
                              sortable
                              resizable
                              flexGrow={1}
                              fixed
                              key={index}
                            >
                              <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                              <MusicScheduleInfoCell
                                dataKey="playlist_title"
                                rowData={(rowData: any) => rowData}
                                onClick={fetchMusicScheduleSongs}
                              />
                            </Column>
                          ) : null}

                          {field.fieldName === t("STORE_GROUP") ? (
                            <Column width={120} resizable key={index}>
                              <CustomHeaderCell>
                                {t("STORE_GROUP")}
                              </CustomHeaderCell>
                              <CustomCell dataKey="group_name">
                                {(rowData: any) => (
                                  <span title={rowData.group_name}>
                                    {displayField(rowData.group_name)}
                                    <br />
                                    {t("IN")}
                                    <br />
                                    {displayField(rowData.region_name)}
                                  </span>
                                )}
                              </CustomCell>
                            </Column>
                          ) : null}
                        </>
                      );
                    })}

                  <Column width={60} fixed="right">
                    <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                    <Cell>
                      {(rowData) => (
                        <MusicScheduleSingleAction
                          musicScheduleData={rowData}
                          onDeleteMusicSchedule={deleteMusicSchedule}
                          onPublishMusicSchedule={publishMusicSchedule}
                          permission={permissionObj}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
              <Footer className="footer">
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  size="xs"
                  layout={["total", "-", "|", "pager"]}
                  total={total}
                  limitOptions={appConstObj.limit_options}
                  limit={rows}
                  activePage={page}
                  onChangePage={onPageChange}
                  onChangeLimit={handleChangeRows}
                />
              </Footer>
            </div>
            <div className="music-schedule-book-separator" aria-hidden="true" />
            <div className="music-schedule-songs-panel">
              {musicScheduleFetched && (
                <MusicScheduleSongs
                  musicScheduleId={selectedMusicSchedule?.id}
                  onDeleteSongs={submitSearchAfterSongChange}
                  songSearchParams={songSearchParams}
                />
              )}
            </div>

            <ToastContainer />
          </div>
        </Content>
      </Container>
    </div>
  );
}

export default MusicSchedules;
